import useFormData from '@client/module/utils/use-form-data';
import Card from 'ui/src/card/card';
import CardContent from 'ui/src/card/card-content';
import CardFooter from 'ui/src/card/card-footer';
import CardHeader from 'ui/src/card/card-header';
import { ContentLayout, PrimaryContent } from 'ui/src/content-layout';
import FormItem from 'ui/src/form/form-item';
import FormLayout from 'ui/src/form/form-layout';
import ToggleInput from 'ui/src/form/input/toggle-input';
import PrimaryButton from 'ui/src/button/primary-button';
import { useNotificationCenter } from 'ui/src/notification/notification-center';
import {
  DeliveryDiscountType,
  MenuSource,
  OnlineOrderBranchSettingItemFragment,
  OpeningTimeServiceType,
} from '@client/graphql/types/graphql';
import ApplicationErrorView from '@client/module/error/application-error-view';
import { useModalManagerContext } from 'ui/src/modal/modal-manager-context';
import SecondaryButton from 'ui/src/button/secondary-button';
import CardTableContent from 'ui/src/card/card-content-table';
import OnlineOrderDeliveryTableDataCreate from '@client/page/management/online-order/setting/component/online-order-delivery-table-data-create.tsx';
import TextInput from 'ui/src/form/input/text-input';
import { useCompanyCurrency } from '@packages/core/company/focused-company-context.ts';
import { currencySymbol, formatRawCurrency, normalizeCurrency } from '@packages/core/utils/currency-formatter.ts';
import OpeningTimeSetup from '@client/page/management/opening-time/opening-time-setup/opening-time-setup.tsx';
import {
  DeliveryDiscountTypeToRaw,
  DeliveryDiscountValueToDisplay,
} from '@client/page/management/online-order/setting/component/delivery-discord-util.tsx';
import { useMenuList } from '@client/page/management/menu/menu-list/logic/use-menu-list.ts';
import AlertError from 'ui/src/alert/alert-error';
import { useCallback, useEffect, useState } from 'react';
import { BlockerFunction, useBlocker } from 'react-router-dom';
import DestroyButton from 'ui/src/button/destroy-button';
import { useDialog } from 'ui/src/modal/use-dialog';
import { useTranslate } from '@tolgee/react';
import useUpdateOnlineOrderConfiguration from '../logic/use-update-online-order-branch-setting';
import DeliveryTable from './delivery-table';
import OnlineOrderDeliveryTableDataUpdate from './online-order-delivery-table-data-update';

// Mirrors the backend ONLINE_ORDER_LEAD_MINUTES fallback used when a branch
// leaves deliveryLeadMinutes unset. Shown as the input placeholder.
const DEFAULT_ORDER_FOR_LATER_LEAD_MINUTES = 60;

interface OnlineOrderBranchSettingForm {
  pickupEnable: boolean;
  deliveryEnable: boolean;
  defaultPreparingTime: number;
  // Empty string = unset → inherit the platform default lead time.
  deliveryLeadMinutes: string;
  minimumOrderAmountForDelivery: string;
  deliveryRadiusLimit: number;
  menuID: string | null;
  deliveryDiscountAmount: string;
  deliveryDiscountType: DeliveryDiscountType;
  autoDeliveryConfirm: boolean;
  autoPickupConfirm: boolean;
}

export default function OnlineOrderBranchSettingEditDelivery(props: {
  onlineOrderBranchSettingData: OnlineOrderBranchSettingItemFragment;
}) {
  // Params
  const { onlineOrderBranchSettingData } = props;

  // Hooks
  const currency = useCompanyCurrency();
  const dialogManager = useModalManagerContext();
  const dialog = useDialog();
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  const [hasBeenChanged, setHasBeenChanged] = useState<boolean>(false);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      return hasBeenChanged && currentLocation.pathname !== nextLocation.pathname;
    },
    [hasBeenChanged],
  );

  const blocker = useBlocker(shouldBlock);
  const { loading: menuLoading } = useMenuList([MenuSource.Restaurant, MenuSource.DigitalMenu]);
  const {
    data: form,
    manualHandle,
    handle,
    setData,
  } = useFormData<OnlineOrderBranchSettingForm>({
    menuID: onlineOrderBranchSettingData.menuID ?? null,
    pickupEnable: onlineOrderBranchSettingData.pickupEnable,
    autoPickupConfirm: onlineOrderBranchSettingData.autoPickupConfirm,
    autoDeliveryConfirm: onlineOrderBranchSettingData.autoDeliveryConfirm,
    deliveryEnable: onlineOrderBranchSettingData.deliveryEnable,
    defaultPreparingTime: onlineOrderBranchSettingData.defaultPreparingTime,
    deliveryLeadMinutes: onlineOrderBranchSettingData.deliveryLeadMinutes?.toString() ?? '',
    deliveryRadiusLimit: onlineOrderBranchSettingData.deliveryRadiusLimit / 1000,
    minimumOrderAmountForDelivery: formatRawCurrency(
      onlineOrderBranchSettingData.minimumOrderAmountForDelivery,
      currency,
    ).value,
    deliveryDiscountType: onlineOrderBranchSettingData.deliveryDiscountType,
    deliveryDiscountAmount: DeliveryDiscountValueToDisplay(
      onlineOrderBranchSettingData.deliveryDiscountType,
      onlineOrderBranchSettingData.deliveryDiscountAmount,
      currency,
    ).toString(),
  });
  const [update, { loading: updateLoading, error: updateError }] = useUpdateOnlineOrderConfiguration();

  const reset = useCallback(() => {
    setData({
      menuID: onlineOrderBranchSettingData.menuID ?? null,
      pickupEnable: onlineOrderBranchSettingData.pickupEnable,
      autoPickupConfirm: onlineOrderBranchSettingData.autoPickupConfirm,
      deliveryEnable: onlineOrderBranchSettingData.deliveryEnable,
      autoDeliveryConfirm: onlineOrderBranchSettingData.autoDeliveryConfirm,
      defaultPreparingTime: onlineOrderBranchSettingData.defaultPreparingTime,
      deliveryLeadMinutes: onlineOrderBranchSettingData.deliveryLeadMinutes?.toString() ?? '',
      deliveryRadiusLimit: onlineOrderBranchSettingData.deliveryRadiusLimit / 1000,
      minimumOrderAmountForDelivery: formatRawCurrency(
        onlineOrderBranchSettingData.minimumOrderAmountForDelivery,
        currency,
      ).value,
      deliveryDiscountType: onlineOrderBranchSettingData.deliveryDiscountType,
      deliveryDiscountAmount: DeliveryDiscountValueToDisplay(
        onlineOrderBranchSettingData.deliveryDiscountType,
        onlineOrderBranchSettingData.deliveryDiscountAmount,
        currency,
      ).toString(),
    });

    blocker.reset?.();
  }, [
    blocker,
    currency,
    onlineOrderBranchSettingData.defaultPreparingTime,
    onlineOrderBranchSettingData.deliveryLeadMinutes,
    onlineOrderBranchSettingData.deliveryEnable,
    onlineOrderBranchSettingData.autoDeliveryConfirm,
    onlineOrderBranchSettingData.deliveryRadiusLimit,
    onlineOrderBranchSettingData.menuID,
    onlineOrderBranchSettingData.minimumOrderAmountForDelivery,
    onlineOrderBranchSettingData.deliveryDiscountAmount,
    onlineOrderBranchSettingData.deliveryDiscountType,
    onlineOrderBranchSettingData.pickupEnable,
    onlineOrderBranchSettingData.autoPickupConfirm,
    setData,
  ]);
  //Detect change between form and data
  useEffect(() => {
    if (
      form.menuID != onlineOrderBranchSettingData.menuID ||
      form.pickupEnable !== onlineOrderBranchSettingData.pickupEnable ||
      form.autoPickupConfirm !== onlineOrderBranchSettingData.autoPickupConfirm ||
      form.deliveryEnable !== onlineOrderBranchSettingData.deliveryEnable ||
      form.autoDeliveryConfirm !== onlineOrderBranchSettingData.autoDeliveryConfirm ||
      form.defaultPreparingTime !== onlineOrderBranchSettingData.defaultPreparingTime ||
      form.deliveryLeadMinutes !==
        (onlineOrderBranchSettingData.deliveryLeadMinutes?.toString() ?? '') ||
      form.deliveryRadiusLimit !== onlineOrderBranchSettingData.deliveryRadiusLimit / 1000 ||
      normalizeCurrency(form.minimumOrderAmountForDelivery, currency) !==
        onlineOrderBranchSettingData.minimumOrderAmountForDelivery ||
      form.deliveryDiscountType !== onlineOrderBranchSettingData.deliveryDiscountType ||
      DeliveryDiscountTypeToRaw(form.deliveryDiscountType, form.deliveryDiscountAmount, currency) !==
        onlineOrderBranchSettingData.deliveryDiscountAmount
    ) {
      setHasBeenChanged(true);
    } else {
      setHasBeenChanged(false);
    }
  }, [currency, form, onlineOrderBranchSettingData]);
  // Data
  const loading = menuLoading || updateLoading;
  // Callbacks
  const handleSubmit = () => {
    dialog.confirmAlert({
      title: t('online-order.setting-page.edit.dialog.title'),
      message: t('online-order.setting-page.edit.dialog.message'),
      textButton: t('online-order.setting-page.edit.dialog.text-button'),
      onConfirm: async () => {
        await update({
          variables: {
            branchID: onlineOrderBranchSettingData.branchID,
            input: {
              menuID: form.menuID,
              pickupEnable: form.pickupEnable,
              autoPickupConfirm: form.autoPickupConfirm,
              deliveryEnable: form.deliveryEnable,
              autoDeliveryConfirm: form.autoDeliveryConfirm,
              defaultPreparingTime: form.defaultPreparingTime,
              deliveryLeadMinutes:
                form.deliveryLeadMinutes.trim() === '' ? null : Number(form.deliveryLeadMinutes),
              deliveryRadiusLimit: form.deliveryRadiusLimit * 1000,
              minimumOrderAmountForDelivery: normalizeCurrency(form.minimumOrderAmountForDelivery, currency),
              deliveryDiscountType: form.deliveryDiscountType,
              deliveryDiscountAmount: DeliveryDiscountTypeToRaw(
                form.deliveryDiscountType,
                form.deliveryDiscountAmount,
                currency,
              ),
            },
          },
        });

        blocker.reset?.();

        showAlert({
          status: 'success',
          title: t('alert.title.success'),
          message: t('online-order.setting-page.edit.alert.success.message'),
        });
      },
    });
  };

  const switchAutoConfirmation = (value: boolean) => {
    dialog.confirmAlert({
      title: t(
        'online-order.setting-page.edit.delivery-auto-confirm-dialog.title',
        'Update delivery auto confirmation',
      ),
      message: t(
        'online-order.setting-page.edit.delivery-auto-confirm-dialog.message',
        'Please check the time configuration in the delivery configuration section.',
      ),
      textButton: t('online-order.setting-page.edit.delivery-auto-confirm-dialog.text-button', 'Confirm'),
      onConfirm: async () => {
        await update({
          variables: {
            branchID: onlineOrderBranchSettingData.branchID,
            input: {
              autoDeliveryConfirm: value,
            },
          },
        });

        blocker.reset?.();

        manualHandle('autoDeliveryConfirm', value);

        showAlert({
          status: 'success',
          title: t('alert.title.success'),
          message: t('online-order.setting-page.edit.alert.success.message'),
        });
      },
    });
  };

  useEffect(() => {
    const handleBlocker = () => {
      dialogManager.showDialog((onClose) => (
        <Card>
          <CardHeader title={t('online-order.setting-page.edit.alert-changed.title')} />

          <CardContent>
            <p className="text-lg text-gray-600">{t('online-order.setting-page.edit.alert-changed.content')}</p>
          </CardContent>

          <CardFooter>
            <div className="w-full flex justify-between">
              <PrimaryButton
                disabled={loading}
                onClick={() => {
                  handleSubmit();
                }}
              >
                {t('common.button.update')}
              </PrimaryButton>
              <div className="flex gap-2">
                <DestroyButton
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  {t('common.button.discard')}
                </DestroyButton>

                <SecondaryButton
                  onClick={() => {
                    onClose();
                  }}
                >
                  {t('common.button.cancel')}
                </SecondaryButton>
              </div>
            </div>
          </CardFooter>
        </Card>
      ));
    };

    if (blocker.state == 'blocked') {
      handleBlocker();
    }

    return () => {
      blocker.reset?.();
    };
  });

  const showCreateModal = () => {
    dialogManager.showDialog((onClose) => (
      <OnlineOrderDeliveryTableDataCreate
        onClose={onClose}
        branchID={onlineOrderBranchSettingData.branchID}
        deliveryData={onlineOrderBranchSettingData.deliveryTableData ?? []}
        autoDeliveryConfirm={form.autoDeliveryConfirm}
      />
    ));
  };

  const showUpdateModal = (deliveryId: string) => {
    dialogManager.showDialog((onClose) => (
      <OnlineOrderDeliveryTableDataUpdate
        onClose={onClose}
        branchID={onlineOrderBranchSettingData.branchID}
        deliveryData={onlineOrderBranchSettingData.deliveryTableData ?? []}
        deliveryId={deliveryId}
        autoDeliveryConfirm={form.autoDeliveryConfirm}
      />
    ));
  };

  return (
    <>
      <ContentLayout>
        <PrimaryContent>
          <ApplicationErrorView error={updateError} />
          {onlineOrderBranchSettingData.deliveryTableData?.length == 0 && (
            <AlertError
              title={t('online-order.setting-page.edit.alert-error.title')}
              message={t('online-order.setting-page.edit.alert-error.message')}
            />
          )}
          <Card>
            <CardHeader title={t('online-order.setting-page.edit.delivery.title', 'Delivery')} withBackButton={true} backTarget={-1} />

            <CardContent>
              <FormLayout>
                {/*DELIVERY*/}
                <FormItem title={t('online-order.setting-page.edit.general.delivery-toggle.title')}>
                  <p className="text-sm text-gray-500 italic my-4">
                    {t('online-order.setting-page.edit.general.delivery-toggle.description')}
                  </p>
                  <ToggleInput
                    value={form.deliveryEnable}
                    onChange={(newVal) => {
                      manualHandle('deliveryEnable', newVal);
                    }}
                  />
                </FormItem>

                {/*<FormItem*/}
                {/*  title={t(*/}
                {/*    'online-order.setting-page.edit.general.auto-delivery-toggle.title',*/}
                {/*    'Auto confirm delivery'*/}
                {/*  )}*/}
                {/*>*/}
                {/*  <p className="text-sm text-gray-500 italic my-4">*/}
                {/*    {t(*/}
                {/*      'online-order.setting-page.edit.general.auto-delivery-toggle.description',*/}
                {/*      'When you enable this toggle, your DELIVERY orders will auto confirm and turn to state "In Progress"'*/}
                {/*    )}*/}
                {/*  </p>*/}
                {/*  <ToggleInput*/}
                {/*    value={form.autoDeliveryConfirm}*/}
                {/*    onChange={(newVal) => {*/}
                {/*      manualHandle('autoDeliveryConfirm', newVal);*/}
                {/*    }}*/}
                {/*  />*/}
                {/*</FormItem>*/}

                <FormItem
                  title={t('online-order.setting-page.edit.general.delivery-min-amount.title')}
                  className="max-w-sm"
                >
                  <TextInput
                    className="w-32"
                    type={'number'}
                    label={'minimumOrderAmountForDelivery'}
                    value={form.minimumOrderAmountForDelivery}
                    name={'minimumOrderAmountForDelivery'}
                    placeholder={t('online-order.setting-page.edit.general.delivery-min-amount.placeholder')}
                    onChange={handle}
                    suffix={<span className="ml-2 text-gray-500">{currencySymbol(currency)}</span>}
                  />
                </FormItem>

                <FormItem title={t('online-order.setting-page.edit.general.delivery-distance-limit.title')}>
                  <p className="text-sm text-gray-500 italic my-4">
                    {t('online-order.setting-page.edit.general.delivery-distance-limit.description')}
                  </p>
                  <TextInput
                    className="w-32"
                    type={'number'}
                    label={'deliveryRadiusLimit'}
                    value={form.deliveryRadiusLimit}
                    name={'deliveryRadiusLimit'}
                    placeholder={t('online-order.setting-page.edit.general.delivery-distance-limit.placeholder')}
                    onChange={handle}
                    suffix={<span className="text-gray-400">km</span>}
                  />
                </FormItem>

                <FormItem
                  title={t(
                    'online-order.setting-page.edit.delivery.order-for-later-lead.title',
                    'Delivery order-for-later lead time',
                  )}
                >
                  <p className="text-sm text-gray-500 italic my-4">
                    {t(
                      'online-order.setting-page.edit.delivery.order-for-later-lead.description',
                      'Minimum minutes ahead a customer can schedule a delivery order. Leave empty to use the default ({default} min).',
                      { default: DEFAULT_ORDER_FOR_LATER_LEAD_MINUTES },
                    )}
                  </p>
                  <TextInput
                    className="w-32"
                    type={'number'}
                    label={'deliveryLeadMinutes'}
                    value={form.deliveryLeadMinutes}
                    name={'deliveryLeadMinutes'}
                    placeholder={DEFAULT_ORDER_FOR_LATER_LEAD_MINUTES.toString()}
                    onChange={handle}
                    suffix={<span className="text-gray-400">min</span>}
                  />
                </FormItem>
              </FormLayout>
            </CardContent>

            <CardFooter>
              <PrimaryButton disabled={loading} onClick={handleSubmit}>
                {t('common.button.update')}
              </PrimaryButton>
            </CardFooter>
          </Card>
          <Card>
            <CardHeader title={t('online-order.setting-page.edit.delivery-setting.title')}>
              <div className="flex gap-2 p-2 border border-gray-200 rounded-lg">
                <p className="text-md font-medium">
                  {t('online-order.setting-page.edit.general.auto-delivery-toggle.title', 'Auto confirm delivery')}
                </p>
                <ToggleInput
                  value={form.autoDeliveryConfirm}
                  onChange={(newVal) => {
                    switchAutoConfirmation(newVal);
                  }}
                />
              </div>
              <SecondaryButton onClick={showCreateModal}>{t('common.button.create')}</SecondaryButton>
            </CardHeader>

            <CardTableContent>
              <DeliveryTable
                autoDeliveryConfirm={form.autoDeliveryConfirm}
                deliveryDataList={onlineOrderBranchSettingData.deliveryTableData ?? []}
                showUpdateModal={showUpdateModal}
              />
            </CardTableContent>
          </Card>
          <OpeningTimeSetup
            title={t('online-order.setting-page.edit.opening-time-setting.delivery.title')}
            service={OpeningTimeServiceType.OnlineOrderDelivery}
          />
        </PrimaryContent>
      </ContentLayout>
    </>
  );
}
